import React, { useState, useEffect } from "react";
import api from "../../utils/Api";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { FileDown, ClipboardList } from "lucide-react";

// ─── Paleta PDF ──────────────────────────────────────────────────────────────
const COLOR_ACCENT  = [255, 122, 0];   // #FF7A00
const COLOR_DAY_BG  = [241, 245, 249]; // slate-100
const COLOR_DAY_TXT = [15,  23,  42];  // slate-900
const COLOR_AUS_BG  = [254, 242, 232]; // naranja muy claro
const COLOR_MUTED   = [100, 116, 139]; // slate-500

// ─── Utilidades ──────────────────────────────────────────────────────────────

const segundosAHMS = (seg) => {
  if (!seg && seg !== 0) return "—";
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  const s = seg % 60;
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
};

const formatearFecha = (fecha) => {
  const dt = new Date(fecha);
  if (isNaN(dt)) return fecha;
  return dt.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const formatearHora = (fecha) => {
  const dt = new Date(fecha);
  if (isNaN(dt)) return "";
  return dt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
};

/** Convierte una fecha ISO a "YYYY-MM-DD" independiente de zona horaria. */
const toDateKey = (isoStr) => {
  const dt = new Date(isoStr);
  if (isNaN(dt)) return isoStr.slice(0, 10);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Encabezado de día en español: "Lunes 25 de mayo de 2026". */
const diaSemana = (dateKey) => {
  const dt = new Date(dateKey + "T00:00:00");
  return dt.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

/** Capitaliza la primera letra. */
const ucfirst = (s) => s.charAt(0).toUpperCase() + s.slice(1);

// ─── Agrupación de fichajes por día ─────────────────────────────────────────

/**
 * Devuelve un Map<dateKey, { entradas, descansos, salidas, duracionTotal }>.
 * Ordenado por fecha ascendente.
*/

function agruparPorDia(registros) {
  const map = new Map();

  for (const r of registros) {
    const key = toDateKey(r.fecha_hora);
    if (!map.has(key)) {
      map.set(key, { entradas: [], descansos: [], salidas: [], duracionTotal: 0 });
    }
    const day = map.get(key);
    if (r.tipo === "entrada")  day.entradas.push(r);
    if (r.tipo === "descanso") day.descansos.push(r);
    if (r.tipo === "salida") {
      day.salidas.push(r);
      day.duracionTotal += r.duracion ?? 0;
    }
  }

  // Orden cronológico
  return new Map([...map.entries()].sort((a, b) => a[0].localeCompare(b[0])));
}

/**
 * Determina si una ausencia aprobada cubre una fecha concreta.
 * Las fechas de la ausencia son YYYY-MM-DD.
*/

function ausenciaParaDia(ausencias, dateKey) {
  return ausencias.find(
    (a) => a.estado === "aprobada" && a.fecha_inicio <= dateKey && a.fecha_fin >= dateKey
  );
}

// ─── Utilidades extra para PDF oficial ──────────────────────────────────────

/** HH:MM a partir de segundos (para el PDF oficial). */
const segAHHMM = (seg) => {
  if (!seg && seg !== 0) return "";
  const h = Math.floor(seg / 3600);
  const m = Math.floor((seg % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/** HH:MM a partir de fecha ISO. */
const horaCorta = (isoStr) => {
  if (!isoStr) return "";
  const dt = new Date(isoStr);
  if (isNaN(dt)) return "";
  return dt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", hour12: false });
};

// ─── PDF Oficial RDL 8/2019 ──────────────────────────────────────────────────

function generarPDFOficial(registros, ausencias, empleado, empresa, fechaInicio, fechaFin) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  const ML  = 8;   // margin left/right
  const PW  = 210;
  const CW  = PW - ML * 2; // 194mm

  // Columnas de la tabla (suma = 194mm)
  const COLS = [
    { key: "dia",     label: "Día\ndel mes",              w: 13 },
    { key: "eMañana", label: "Mañana",                    w: 24, group: "Hora Entrada" },
    { key: "eTarde",  label: "Tarde",                     w: 24, group: "Hora Entrada" },
    { key: "sMañana", label: "Mañana",                    w: 24, group: "Hora de Salida" },
    { key: "sTarde",  label: "Tarde",                     w: 24, group: "Hora de Salida" },
    { key: "total",   label: "Total Horas\nJornada",      w: 26 },
    { key: "ord",     label: "Horas\nOrdinarias",         w: 26 },
    { key: "firma",   label: "FIRMA",                     w: 33 },
  ];

  // Precalcular posición X de cada columna
  const colX = [];
  let cx = ML;
  for (const c of COLS) { colX.push(cx); cx += c.w; }

  // Agrupar fichajes
  const diasMap = agruparPorDia(registros);

  // Determinar rango de meses a renderizar
  let start = fechaInicio ? new Date(fechaInicio + "T00:00:00") : null;
  let end   = fechaFin   ? new Date(fechaFin   + "T00:00:00") : null;
  if (!start && diasMap.size > 0) {
    const keys = [...diasMap.keys()].sort();
    start = new Date(keys[0] + "T00:00:00");
    end   = new Date(keys[keys.length - 1] + "T00:00:00");
  }
  if (!start) return;
  if (!end) end = start;

  const months = [];
  let cur = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (cur <= endMonth) {
    months.push({ year: cur.getFullYear(), month: cur.getMonth() });
    cur.setMonth(cur.getMonth() + 1);
  }

  months.forEach((mo, moIdx) => {
    if (moIdx > 0) doc.addPage();

    const { year, month } = mo;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const periodoStr  = ucfirst(
      new Date(year, month, 1).toLocaleDateString("es-ES", { month: "long", year: "numeric" })
    );
    const fechaHoyStr = new Date().toLocaleDateString("es-ES");

    let y = ML;

    // ── Título ─────────────────────────────────────────────────────────────
    doc.setFillColor(30, 56, 136);
    doc.rect(ML, y, CW, 8, "F");
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("REGISTRO DIARIO DE JORNADA EN TRABAJADORES", PW / 2, y + 5.5, { align: "center" });
    y += 8;

    // ── Sub-cabecera EMPRESA / TRABAJADOR ──────────────────────────────────
    const half = CW / 2;
    doc.setFillColor(180, 194, 232);
    doc.rect(ML, y, half, 6, "FD");
    doc.rect(ML + half, y, half, 6, "FD");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text("EMPRESA",     ML + half / 2,        y + 4, { align: "center" });
    doc.text("TRABAJADOR",  ML + half + half / 2,  y + 4, { align: "center" });
    y += 6;

    // ── Filas de datos empresa/trabajador ─────────────────────────────────
    const infoRows = [
      { lbl1: "Razón Social:", val1: empresa?.nombre_fiscal || "",     lbl2: "Nombre:",  val2: `${empleado?.nombre || ""} ${empleado?.apellidos || ""}`.trim() },
      { lbl1: "CIF:",                   val1: empresa?.cif || "",               lbl2: "NIF:",     val2: empleado?.dni || "" },
      { lbl1: "C.C.C.:",               val1: "",                               lbl2: "NAF:",     val2: "" },
      { lbl1: "Período:", val1: periodoStr,                      lbl2: "Fecha:",   val2: fechaHoyStr },
    ];

    const IRH = 5;
    doc.setFontSize(7.5);
    for (const row of infoRows) {
      doc.setDrawColor(160, 160, 180);
      doc.rect(ML,        y, half, IRH, "S");
      doc.rect(ML + half, y, half, IRH, "S");

      doc.setFont("helvetica", "bold"); doc.setTextColor(80, 80, 100);
      doc.text(row.lbl1, ML + 1.5, y + 3.5);
      doc.setFont("helvetica", "normal"); doc.setTextColor(0, 0, 0);
      doc.text(row.val1, ML + 1.5 + doc.getTextWidth(row.lbl1) + 1, y + 3.5);

      doc.setFont("helvetica", "bold"); doc.setTextColor(80, 80, 100);
      doc.text(row.lbl2, ML + half + 1.5, y + 3.5);
      doc.setFont("helvetica", "normal"); doc.setTextColor(0, 0, 0);
      doc.text(row.val2, ML + half + 1.5 + doc.getTextWidth(row.lbl2) + 1, y + 3.5);

      y += IRH;
    }

    // ── Cabecera de la tabla ───────────────────────────────────────────────
    const HDR_H = 11;
    doc.setFillColor(210, 218, 240);
    doc.rect(ML, y, CW, HDR_H, "FD");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7);
    doc.setTextColor(0, 0, 0);
    doc.setDrawColor(120, 130, 160);

    // Grupos "Hora Entrada" y "Hora de Salida"
    const entradaX = colX[1];
    const entradaW = COLS[1].w + COLS[2].w; // 48mm
    const salidaX  = colX[3];
    const salidaW  = COLS[3].w + COLS[4].w; // 48mm

    doc.text("Hora Entrada",   entradaX + entradaW / 2, y + 4,   { align: "center" });
    doc.text("Hora de Salida", salidaX  + salidaW  / 2, y + 4,   { align: "center" });

    // Línea separadora entre grupo y sublabel
    doc.setLineWidth(0.2);
    doc.line(entradaX, y + 5.5, entradaX + entradaW, y + 5.5);
    doc.line(salidaX,  y + 5.5, salidaX  + salidaW,  y + 5.5);

    // Sublabels Mañana/Tarde
    doc.setFont("helvetica", "normal");
    doc.text("Mañana", colX[1] + COLS[1].w / 2, y + 9, { align: "center" });
    doc.text("Tarde",  colX[2] + COLS[2].w / 2, y + 9, { align: "center" });
    doc.text("Mañana", colX[3] + COLS[3].w / 2, y + 9, { align: "center" });
    doc.text("Tarde",  colX[4] + COLS[4].w / 2, y + 9, { align: "center" });

    // Cabeceras de columnas simples (multilínea centrada)
    const simples = [
      { i: 0, lines: ["Día del mes"] },
      { i: 5, lines: ["Total Horas", "Jornada"] },
      { i: 6, lines: ["Horas", "Ordinarias"] },
      { i: 7, lines: ["FIRMA"] },
    ];
    doc.setFont("helvetica", "bold");
    for (const s of simples) {
      const cx2 = colX[s.i] + COLS[s.i].w / 2;
      if (s.lines.length === 1) {
        doc.text(s.lines[0], cx2, y + 6.5, { align: "center" });
      } else {
        doc.text(s.lines[0], cx2, y + 4,   { align: "center" });
        doc.text(s.lines[1], cx2, y + 8.5, { align: "center" });
      }
    }

    // Bordes de cada celda de cabecera
    doc.setLineWidth(0.3);
    for (let i = 0; i < COLS.length; i++) {
      doc.rect(colX[i], y, COLS[i].w, HDR_H, "S");
    }
    y += HDR_H;

    // ── Filas de días ──────────────────────────────────────────────────────
    const ROW_H = 5.2;
    let totalMesSeg = 0;

    for (let d = 1; d <= daysInMonth; d++) {
      const dateKey = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const dayData = diasMap.get(dateKey);
      const ausencia = ausenciaParaDia(ausencias, dateKey);

      // Fondo alternado
      if (d % 2 === 0) {
        doc.setFillColor(245, 247, 252);
        doc.rect(ML, y, CW, ROW_H, "F");
      }

      // Calcular valores
      let eMañana = "", eTarde = "", sMañana = "", sTarde = "", total = "", ord = "";

      if (dayData) {
        // Ordenar por hora para garantizar orden cronológico
        const entradas = [...dayData.entradas].sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora));
        const salidas  = [...dayData.salidas ].sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora));
        const descanso = dayData.descansos[0];

        if (salidas.length >= 2) {
          // ── Turno partido: dos pares entrada/salida ──────────────────────
          // Entrada Mañana / Salida Mañana / Entrada Tarde / Salida Tarde
          eMañana = entradas[0] ? horaCorta(entradas[0].fecha_hora) : "";
          sMañana = horaCorta(salidas[0].fecha_hora);
          eTarde  = entradas[1] ? horaCorta(entradas[1].fecha_hora) : "";
          sTarde  = horaCorta(salidas[1].fecha_hora);
        } else if (descanso) {
          // ── Turno partido vía descanso ───────────────────────────────────
          eMañana = entradas[0] ? horaCorta(entradas[0].fecha_hora) : "";
          sMañana = horaCorta(descanso.fecha_hora);
          eTarde  = descanso.fin ? horaCorta(descanso.fin) : "";
          sTarde  = salidas[0]  ? horaCorta(salidas[0].fecha_hora) : "";
        } else {
          // ── Turno continuo: una entrada y una salida ─────────────────────
          eMañana = entradas[0] ? horaCorta(entradas[0].fecha_hora) : "";
          sTarde  = salidas[0]  ? horaCorta(salidas[0].fecha_hora)  : "";
        }

        // Total del día = suma de todas las salidas
        const durDia = salidas.reduce((acc, s) => acc + (s.duracion ?? 0), 0);
        if (durDia > 0) {
          totalMesSeg += durDia;
          total = segAHHMM(durDia);
          ord   = total;
        }
      }

      // Texto de la fila
      doc.setFontSize(7);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(20, 20, 20);

      // Día
      doc.text(String(d), colX[0] + COLS[0].w / 2, y + 3.6, { align: "center" });

      if (ausencia && !dayData) {
        // Ausencia sin fichaje: mostrar en color
        doc.setTextColor(180, 80, 0);
        doc.setFont("helvetica", "italic");
        const tipoAus = { vacaciones: "Vacaciones", enfermedad: "Baja médica", permiso: "Permiso" }[ausencia.tipo] ?? ausencia.tipo;
        doc.text(tipoAus, colX[1] + 1, y + 3.6);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(20, 20, 20);
      } else {
        doc.text(eMañana, colX[1] + COLS[1].w / 2, y + 3.6, { align: "center" });
        doc.text(eTarde,  colX[2] + COLS[2].w / 2, y + 3.6, { align: "center" });
        doc.text(sMañana, colX[3] + COLS[3].w / 2, y + 3.6, { align: "center" });
        doc.text(sTarde,  colX[4] + COLS[4].w / 2, y + 3.6, { align: "center" });
        doc.text(total,   colX[5] + COLS[5].w / 2, y + 3.6, { align: "center" });
        doc.text(ord,     colX[6] + COLS[6].w / 2, y + 3.6, { align: "center" });
      }

      // Bordes fila
      doc.setDrawColor(190, 195, 215);
      doc.setLineWidth(0.2);
      for (let i = 0; i < COLS.length; i++) {
        doc.rect(colX[i], y, COLS[i].w, ROW_H, "S");
      }

      y += ROW_H;
    }

    // ── Fila Total Mes ─────────────────────────────────────────────────────
    doc.setFillColor(180, 194, 232);
    doc.setLineWidth(0.3);
    doc.rect(ML, y, CW, ROW_H + 0.8, "FD");
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text("Total Mes", colX[0] + COLS[0].w / 2, y + 4.2, { align: "center" });
    if (totalMesSeg > 0) {
      const tot = segAHHMM(totalMesSeg);
      doc.text(tot, colX[5] + COLS[5].w / 2, y + 4.2, { align: "center" });
      doc.text(tot, colX[6] + COLS[6].w / 2, y + 4.2, { align: "center" });
    }
    y += ROW_H + 0.8 + 5;

    // ── Texto legal ────────────────────────────────────────────────────────
    doc.setFontSize(7);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(
      "En cumplimiento de la obligación establecida en el Art. 10 del Real Decreto Ley 8 / 2019",
      ML, y
    );
    y += 6;

    // ── Cajas de firma ─────────────────────────────────────────────────────
    const sigW = CW / 2 - 6;
    const sigH = 20;
    doc.setDrawColor(100, 100, 130);
    doc.setLineWidth(0.4);
    doc.rect(ML,              y, sigW, sigH, "D");
    doc.rect(ML + sigW + 12,  y, sigW, sigH, "D");
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.text("Firma de la Empresa",   ML + sigW / 2,             y + 4, { align: "center" });
    doc.text("Firma del Trabajador",  ML + sigW + 12 + sigW / 2, y + 4, { align: "center" });
  });

  doc.save(`registro_jornada_${empleado?.nombre || "empleado"}_${fechaInicio || "periodo"}.pdf`);
}

// ─── Motor de PDF ────────────────────────────────────────────────────────────

/**
 * Genera un PDF agrupado por día.
 *
 * Estructura por página/bloque:
 *   ┌──────────────────────────────────────────┐
 *   │  LUNES 25 DE MAYO DE 2026                │  ← cabecera de día
 *   │  Entrada  09:03:00   –                   │
 *   │  Descanso 11:30:00   Fin 12:00:00 (30m)  │
 *   │  Salida   18:05:00   Neto: 08:32:00       │
 *   │  ─────────────────────────────────────── │
 *   │  Total día: 08:32:00                      │
 *   └──────────────────────────────────────────┘
 *
 * Si hay ausencia aprobada y no hay fichajes ese día:
 *   ┌──────────────────────────────────────────┐
 *   │  MARTES 26 DE MAYO DE 2026               │
 *   │  ✦ Ausente: Vacaciones                   │
 *   └──────────────────────────────────────────┘
 */

function generarPDFAgrupado(registros, ausencias, empleado, empresa, fechaInicio, fechaFin) {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const PW = doc.internal.pageSize.getWidth();

  // ── Cabecera del documento ──────────────────────────────────────────────
  doc.setFillColor(...COLOR_ACCENT);
  doc.rect(0, 0, PW, 22, "F");

  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.text("Listado de Horas Trabajadas", 14, 13);

  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.text(`Empresa: ${empresa?.nombre_fiscal || "—"}`, 14, 19);
  doc.text(`Empleado: ${empleado?.nombre || "—"} ${empleado?.apellidos || ""}`, PW / 2, 19);

  // Periodo en la esquina derecha
  const periodoStr = `${fechaInicio || "—"}  →  ${fechaFin || "—"}`;
  doc.text(periodoStr, PW - 14, 19, { align: "right" });

  // ── Agrupar datos ────────────────────────────────────────────────────────
  const diasMap = agruparPorDia(registros);

  // Recoger todas las fechas que tienen fichaje o ausencia aprobada en el rango
  const fechasConFichaje = new Set(diasMap.keys());

  // Construir el set de días a mostrar (unión de días con fichaje + días con ausencia aprobada en el rango)
  const fechasAusencias = ausencias
    .filter((a) => a.estado === "aprobada")
    .reduce((acc, a) => {
      // Expandir el rango de la ausencia día a día
      const current = new Date(a.fecha_inicio + "T00:00:00");
      const end     = new Date(a.fecha_fin + "T00:00:00");
      while (current <= end) {
        const key = toDateKey(current.toISOString());
        if (!fechasConFichaje.has(key)) acc.add(key);
        current.setDate(current.getDate() + 1);
      }
      return acc;
    }, new Set());

  // Fusionar y ordenar
  const todasFechas = [...new Set([...fechasConFichaje, ...fechasAusencias])].sort();

  if (todasFechas.length === 0) {
    doc.setFontSize(11);
    doc.setTextColor(...COLOR_MUTED);
    doc.text("No hay registros para el período seleccionado.", 14, 40);
    doc.save(`horas_${empleado?.nombre || "empleado"}.pdf`);
    return;
  }

  let totalSegundosGlobal = 0;
  let y = 30; // cursor Y

  const checkPageBreak = (needed = 20) => {
    if (y + needed > doc.internal.pageSize.getHeight() - 14) {
      doc.addPage();
      y = 16;
    }
  };

  for (const dateKey of todasFechas) {
    checkPageBreak(40);

    const dayData  = diasMap.get(dateKey);
    const ausencia = ausenciaParaDia(ausencias, dateKey);

    // ── Cabecera del día ──────────────────────────────────────────────────
    doc.setFillColor(...COLOR_DAY_BG);
    doc.roundedRect(12, y, PW - 24, 8, 2, 2, "F");

    doc.setFontSize(9.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(...COLOR_DAY_TXT);
    doc.text(ucfirst(diaSemana(dateKey)), 16, y + 5.5);

    y += 11;

    // ── Caso: solo ausencia, sin fichajes ────────────────────────────────
    if (!dayData && ausencia) {
      doc.setFillColor(...COLOR_AUS_BG);
      doc.rect(12, y, PW - 24, 8, "F");

      doc.setFontSize(9);
      doc.setFont("helvetica", "italic");
      doc.setTextColor(...COLOR_ACCENT);
      const tipoAus = { vacaciones: "Vacaciones", enfermedad: "Baja médica", permiso: "Permiso" }[ausencia.tipo] ?? ausencia.tipo;
      doc.text(`  ✦  Ausente: ${tipoAus}`, 16, y + 5.5);

      y += 11;
      continue;
    }

    if (!dayData) continue;

    // ── Construcción de filas para la tabla del día ───────────────────────
    const rows = [];

    // Entradas
    for (const e of dayData.entradas) {
      rows.push([
        { content: "Entrada", styles: { fontStyle: "bold", textColor: COLOR_ACCENT } },
        formatearHora(e.fecha_hora),
        "—",
        "—",
      ]);
    }

    // Descansos
    for (const d of dayData.descansos) {
      const durDes = d.duracion ? `(${segundosAHMS(d.duracion)})` : "(en curso)";
      rows.push([
        { content: "Descanso", styles: { fontStyle: "bold", textColor: [234, 179, 8] } },
        formatearHora(d.fecha_hora),
        d.fin ? formatearHora(d.fin) : "—",
        durDes,
      ]);
    }

    // Salidas
    for (const s of dayData.salidas) {
      totalSegundosGlobal += s.duracion ?? 0;
      rows.push([
        { content: "Salida", styles: { fontStyle: "bold", textColor: [239, 68, 68] } },
        formatearHora(s.fecha_hora),
        "—",
        { content: `Neto: ${segundosAHMS(s.duracion)}`, styles: { fontStyle: "bold" } },
      ]);
    }

    // Si había ausencia además de fichajes
    if (ausencia) {
      const tipoAus = { vacaciones: "Vacaciones", enfermedad: "Baja médica", permiso: "Permiso" }[ausencia.tipo] ?? ausencia.tipo;
      rows.push([
        { content: `✦ Ausencia: ${tipoAus}`, colSpan: 4, styles: { fontStyle: "italic", textColor: COLOR_ACCENT, fillColor: COLOR_AUS_BG } },
      ]);
    }

    autoTable(doc, {
      startY: y,
      head:   [["Tipo", "Inicio", "Fin", "Duración / Total"]],
      body:   rows,
      theme:  "plain",
      margin: { left: 12, right: 12 },
      headStyles: {
        fillColor:   [226, 232, 240],
        textColor:   COLOR_DAY_TXT,
        fontStyle:   "bold",
        fontSize:    8,
        cellPadding: 2.5,
      },
      bodyStyles: {
        fontSize:    8.5,
        cellPadding: 2.5,
        textColor:   COLOR_DAY_TXT,
      },
      columnStyles: {
        0: { cellWidth: 28 },
        1: { cellWidth: 32 },
        2: { cellWidth: 32 },
        3: { cellWidth: "auto" },
      },
      didDrawPage: (data) => {
        y = data.cursor.y + 2;
      },
    });

    y = doc.lastAutoTable.finalY + 2;

    // Total del día
    if (dayData.duracionTotal > 0) {
      doc.setFontSize(8.5);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(...COLOR_ACCENT);
      doc.text(`Total día: ${segundosAHMS(dayData.duracionTotal)}`, PW - 14, y + 4, { align: "right" });
    }

    y += 9;
  }

  // ── Pie de totales globales ───────────────────────────────────────────────
  checkPageBreak(18);

  doc.setDrawColor(...COLOR_ACCENT);
  doc.setLineWidth(0.4);
  doc.line(12, y, PW - 12, y);
  y += 5;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...COLOR_DAY_TXT);
  doc.text("TOTAL HORAS TRABAJADAS:", 14, y + 5);
  doc.setTextColor(...COLOR_ACCENT);
  doc.text(segundosAHMS(totalSegundosGlobal), PW - 14, y + 5, { align: "right" });

  doc.save(`horas_${empleado?.nombre || "empleado"}_agrupado.pdf`);
}

// ─── Componente ──────────────────────────────────────────────────────────────

export default function ListadoHorasT() {
  const [state, setState] = useState({
    empleados: [], filtroEmpleado: "", fechaInicio: "", fechaFin: "",
    listadoHoras: [], loading: false, error: null,
    paginaActual: 1, registrosPorPagina: 10,
  });
  const [empresa, setEmpresa] = useState(null);

  // ── Utilidades ────────────────────────────────────────────────────────────

  const segundosAHMSUI = (seg) => {
    if (!seg && seg !== 0) return "—";
    const h = Math.floor(seg / 3600);
    const m = Math.floor((seg % 3600) / 60);
    const s = seg % 60;
    return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":");
  };

  const formatearFechaUI = (fecha) => {
    const dt = new Date(fecha);
    if (isNaN(dt)) return fecha;
    return dt.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
  };

  const formatearHoraUI = (fecha) => {
    const dt = new Date(fecha);
    if (isNaN(dt)) return "";
    return dt.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  };

  // ── API ───────────────────────────────────────────────────────────────────

  const fetchEmpleados = async () => {
    try {
      const response = await api.get("/usuarios");
      setState((prev) => ({ ...prev, empleados: response.data }));
    } catch {
      setState((prev) => ({ ...prev, error: "Error cargando empleados" }));
    }
  };

  const fetchEmpresa = async () => {
    try {
      const res = await api.get("/empresa");
      setEmpresa(res.data);
    } catch {}
  };

  const fetchListadoHoras = async (filtros = {}) => {
    setState((prev) => ({ ...prev, loading: true }));
    try {
      const response = await api.post("/fichajes/listado", filtros);
      const ordenado = [...response.data.data].sort((a, b) => a.id - b.id);
      setState((prev) => ({ ...prev, listadoHoras: ordenado, paginaActual: 1, error: null }));
    } catch {
      setState((prev) => ({ ...prev, error: "Error cargando listado de horas" }));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => { fetchEmpleados(); fetchEmpresa(); }, []);

  // ── PDF agrupado ──────────────────────────────────────────────────────────

  const handleGenerarPDF = async () => {
    const { filtroEmpleado, fechaInicio, fechaFin, empleados } = state;

    if (!filtroEmpleado) {
      setState((prev) => ({ ...prev, error: "Seleccione un empleado" }));
      return;
    }

    try {
      // 1. Fichajes del periodo
      const fichajesRes = await api.post("/fichajes/listado", {
        empleado:      filtroEmpleado,
        fecha_inicio:  fechaInicio,
        fecha_fin:     fechaFin,
      });
      const registros = fichajesRes.data.data;

      // 2. Ausencias aprobadas de la empresa (filtramos por empleado en JS)
      let ausencias = [];
      try {
        const ausRes = await api.get("/ausencias");
        const todas  = Array.isArray(ausRes.data) ? ausRes.data : [];
        // Filtrar por el empleado seleccionado y el rango de fechas
        ausencias = todas.filter((a) => {
          if (String(a.usuario_id) !== String(filtroEmpleado)) return false;
          if (a.estado !== "aprobada") return false;
          // Intersección con el rango buscado
          if (fechaInicio && a.fecha_fin   < fechaInicio) return false;
          if (fechaFin    && a.fecha_inicio > fechaFin)   return false;
          return true;
        });
      } catch {
        // No es crítico — el PDF se genera igualmente sin ausencias
      }

      const empleado = empleados.find((e) => e.id === Number(filtroEmpleado));

      generarPDFAgrupado(registros, ausencias, empleado, empresa, fechaInicio, fechaFin);
    } catch {
      setState((prev) => ({ ...prev, error: "Error generando PDF" }));
    }
  };

  // ── PDF Oficial RDL 8/2019 ──
  const handleGenerarPDFOficial = async () => {
    const { filtroEmpleado, fechaInicio, fechaFin, empleados } = state;

    if (!filtroEmpleado) {
      setState((prev) => ({ ...prev, error: "Seleccione un empleado" }));
      return;
    }

    try {
      const fichajesRes = await api.post("/fichajes/listado", {
        empleado:     filtroEmpleado,
        fecha_inicio: fechaInicio,
        fecha_fin:    fechaFin,
      });
      const registros = fichajesRes.data.data;

      let ausencias = [];
      try {
        const ausRes = await api.get("/ausencias");
        const todas  = Array.isArray(ausRes.data) ? ausRes.data : [];
        ausencias = todas.filter((a) => {
          if (String(a.usuario_id) !== String(filtroEmpleado)) return false;
          if (a.estado !== "aprobada") return false;
          if (fechaInicio && a.fecha_fin   < fechaInicio) return false;
          if (fechaFin    && a.fecha_inicio > fechaFin)   return false;
          return true;
        });
      } catch { /* no crítico */ }

      const empleado = empleados.find((e) => e.id === Number(filtroEmpleado));
      generarPDFOficial(registros, ausencias, empleado, empresa, fechaInicio, fechaFin);
    } catch (err) {
      console.error("Error generando PDF oficial:", err);
      setState((prev) => ({ ...prev, error: `Error generando PDF oficial: ${err?.message || err}` }));
    }
  };

  // ── Form ──────────────────────────────────────────────────────────────────

  const handleSubmit = (e) => {
    e.preventDefault();
    const { filtroEmpleado, fechaInicio, fechaFin } = state;

    if (!filtroEmpleado) {
      setState((prev) => ({ ...prev, error: "Seleccione un empleado" }));
      return;
    }
    if (fechaInicio && fechaFin && fechaInicio > fechaFin) {
      setState((prev) => ({ ...prev, error: "La fecha inicio no puede ser mayor que la fecha fin" }));
      return;
    }

    fetchListadoHoras({ empleado: filtroEmpleado, fecha_inicio: fechaInicio, fecha_fin: fechaFin });
  };

  // ── Paginación ────────────────────────────────────────────────────────────

  const { paginaActual, registrosPorPagina, listadoHoras } = state;
  const indiceUltimo  = paginaActual * registrosPorPagina;
  const indicePrimero = indiceUltimo - registrosPorPagina;
  const registrosPaginados = listadoHoras.slice(indicePrimero, indiceUltimo);
  const totalPaginas = Math.ceil(listadoHoras.length / registrosPorPagina);

  const totalSegundosUI = listadoHoras
    .filter((r) => r.duracion)
    .reduce((acc, r) => acc + r.duracion, 0);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div>
      <h1 className="tn-page-title">Horas trabajadas</h1>
      <p className="tn-page-sub">Consulta el registro por empleado y exporta el PDF agrupado por día</p>

      <div className="tn-card" style={{ padding: 20, marginBottom: 20 }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 14, marginBottom: 14 }}>
            <div>
              <label className="tn-label">Empleado</label>
              <select className="tn-input tn-select" value={state.filtroEmpleado}
                onChange={(e) => setState((prev) => ({ ...prev, filtroEmpleado: e.target.value }))}>
                <option value="">Seleccione un empleado</option>
                {state.empleados.map((e) => (
                  <option key={e.id} value={e.id}>{e.nombre} {e.apellidos}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="tn-label">Fecha inicio</label>
              <input type="date" className="tn-input" value={state.fechaInicio}
                onChange={(e) => setState((prev) => ({ ...prev, fechaInicio: e.target.value }))} />
            </div>
            <div>
              <label className="tn-label">Fecha fin</label>
              <input type="date" className="tn-input" value={state.fechaFin}
                onChange={(e) => setState((prev) => ({ ...prev, fechaFin: e.target.value }))} />
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="submit" className="tn-btn tn-btn--primary" disabled={state.loading}>
              {state.loading ? "Cargando…" : "Consultar"}
            </button>
            <button type="button" className="tn-btn tn-btn--ghost" onClick={handleGenerarPDF}
              disabled={!listadoHoras.length && !state.filtroEmpleado}
              style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <FileDown size={15} /> <span>PDF por día</span>
            </button>
            <button type="button" className="tn-btn tn-btn--ghost" onClick={handleGenerarPDFOficial}
              disabled={!state.filtroEmpleado}
              style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <ClipboardList size={15} /> <span>PDF Oficial (RDL 8/2019)</span>
            </button>
          </div>
        </form>
      </div>

      {state.error && (
        <div className="tn-alert tn-alert--danger" style={{ marginBottom: 16 }}>{state.error}</div>
      )}

      {listadoHoras.length > 0 && (
        <div style={{ fontSize: 13, color: "var(--clr-muted)", marginBottom: 12 }}>
          <strong>{listadoHoras.length}</strong> registros — Total horas netas:{" "}
          <strong style={{ color: "var(--clr-accent)" }}>{segundosAHMSUI(totalSegundosUI)}</strong>
        </div>
      )}

      <div className="tn-card">
        <div className="tn-table-wrap">
          <table className="tn-table">
            <thead>
              <tr><th>Fecha</th><th>Tipo</th><th>Hora</th><th>Duración neta</th></tr>
            </thead>
            <tbody>
              {registrosPaginados.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{ textAlign: "center", padding: "40px 20px", color: "var(--clr-muted)" }}>
                    Sin registros — aplica los filtros y pulsa Consultar
                  </td>
                </tr>
              ) : registrosPaginados.map((r) => (
                <tr key={r.id}>
                  <td data-label="Fecha">{formatearFechaUI(r.fecha_hora)}</td>
                  <td data-label="Tipo" style={{ textTransform: "capitalize" }}>{r.tipo}</td>
                  <td data-label="Hora">{formatearHoraUI(r.fecha_hora)}</td>
                  <td data-label="Duración neta">
                    {r.duracion ? segundosAHMSUI(r.duracion) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPaginas > 1 && (
          <div className="tn-pagination" style={{ padding: "12px 16px", borderTop: "1px solid var(--clr-border)" }}>
            <button className="tn-page-btn" disabled={paginaActual === 1}
              onClick={() => setState(prev => ({ ...prev, paginaActual: prev.paginaActual - 1 }))}>‹</button>
            {[...Array(totalPaginas)].map((_, i) => (
              <button key={i}
                className={`tn-page-btn${paginaActual === i + 1 ? " active" : ""}`}
                onClick={() => setState(prev => ({ ...prev, paginaActual: i + 1 }))}>
                {i + 1}
              </button>
            ))}
            <button className="tn-page-btn" disabled={paginaActual === totalPaginas}
              onClick={() => setState(prev => ({ ...prev, paginaActual: prev.paginaActual + 1 }))}>›</button>
          </div>
        )}
      </div>
    </div>
  );
}
